/**
 * Class applied to the Plate email editable (`PlateContent`).
 *
 * Two kinds of rule live here, and the distinction is load-bearing:
 *
 * 1. Layout/chrome utilities and DIRECT-CHILD `[&>…]` variants, which style
 *    the top-level blocks the USER types. That content is app-authored, so
 *    app typography is the right call. The `[&>…]:text-sm` variants restore
 *    the 14px body size that `prose-sm` used to supply (Tailwind Typography's
 *    `prose-sm` base font-size is 0.875rem = 14px); being
 *    direct-child scoped they never reach the provider signature. Headings,
 *    lists, and blockquotes render as real elements, so their `[&>h1]`/
 *    `[&>ul]`/`[&>blockquote]`/etc. selectors match directly. Paragraphs are
 *    the exception: Plate renders a paragraph block as `<div class="slate-p">`
 *    (no `<p>` tag), so paragraphs are targeted via `[&>.slate-p]` instead.
 * 2. Nothing that reaches into descendants. A draft also carries markup we
 *    did NOT author — the provider signature, quoted replies, pasted mail —
 *    and that must render the way a mail client renders it.
 * 3. ONE inherited default: a bare `text-sm` on the container itself. This is
 *    the base size everything in the editable falls back to, and it is the
 *    only font-size declaration allowed to be visible to the signature.
 *
 * On (3): the direct-child variants in (1) put typed paragraphs at 14px, but a
 * signature is injected as raw HTML two levels down, so `[&>.slate-p]` cannot
 * reach it — by design. A signature that carries no font-size of its own (the
 * common shape is bare `<div>`/`<span>`, with no `<p>` for the
 * `email-content-baseline` revert to even act on) therefore inherited straight
 * past the editable to the app root and rendered at 16px, beside 14px typed
 * text. Sent mail already renders the two at the SAME size, so the editor was
 * the odd one out and the composer was lying about the result.
 *
 * A container default fixes that by inheritance, which is the weakest tool
 * available and importantly NOT the `prose` mistake below: inheritance loses to
 * any font-size the signature declares, so provider markup that styles itself
 * still wins and still renders as its mail client would. Only markup that
 * expresses no opinion picks this up — and for that markup, matching the body
 * is the honest preview. Do NOT escalate this into a descendant selector.
 *
 * `email-content-baseline` (design-system.css) reverts Tailwind's global
 * preflight for email content, so provider HTML keeps its bullets, heading
 * sizes and natural table widths. It is a floor at zero specificity, not a
 * new set of opinions.
 *
 * ⚠️ THE SPACING RULES HERE ARE THE SOURCE OF TRUTH FOR OUTGOING EMAIL.
 * `[&>*]:mb-2` (8px per top-level block) and `[&>ul]:space-y-1` (4px between
 * list items) are what the composer shows the sender, and outgoing mail is
 * made to match — mail clients supply no usable `<p>` margin of their own
 * (Gmail supplies none at all), so without explicit spacing a multi-paragraph
 * email arrives as one wall of text.
 *
 * Nothing in this editor emits those styles into the serialized HTML. They are
 * applied in exactly ONE place — `normalize_outgoing_html_for_clients` in
 * apps/email/services/body_composition.py, a final step at the send boundary —
 * so one implementation covers composer-authored AND agent-drafted mail, and
 * one RuntimeConfig key (`email.block_spacing_enabled`) switches all of it off.
 *
 * Changing the spacing here therefore changes what recipients see. Update
 * BLOCK_MARGIN_BOTTOM_PX / LIST_ITEM_MARGIN_BOTTOM_PX in body_composition.py in
 * the same PR — test_block_spacing_matches_editor reads THIS file and fails if
 * the two drift.
 *
 * Do NOT reintroduce `prose`/`prose-sm` or any `[&_…]` descendant variant —
 * both reach into provider markup. `prose` restyled a real table-based
 * signature to 528px wide / 12px / 8px cell padding, against 246px / 16px / 0
 * naturally, breaking the WYSIWYG promise that draft-time signature insertion
 * exists to provide. `__tests__/constants.test.ts` pins this.
 */
export const PLATE_CONTENT_CLASS =
  'email-content-baseline w-full h-full px-4 pt-2 pb-6 text-sm focus:outline-none max-w-none break-words [&>*]:mb-2 [&>*:last-child]:mb-0 [&>[data-plate-image]]:mb-1 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:leading-tight [&>h2]:text-xl [&>h2]:font-semibold [&>h2]:leading-tight [&>h3]:text-lg [&>h3]:font-medium [&>h3]:leading-tight [&>blockquote]:border-l-4 [&>blockquote]:border-border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-base-content/70 [&>blockquote]:bg-base-200 [&>blockquote]:text-sm [&>.slate-p]:text-sm/relaxed [&>.slate-p]:text-base-content [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-1 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:space-y-1 [&>ul>li]:text-sm/relaxed [&>ul>li]:text-base-content [&>ol>li]:text-sm/relaxed [&>ol>li]:text-base-content selection:bg-utility-brand-100';

// Must stay byte-compatible with `wrap_signature_block` in
// apps/email/services/body_composition.py — the parser interception in
// parse-html-to-plate-value.ts keys off this attribute, and the send path
// recognises a draft-carried signature by the same marker. Exported so
// EmailComposePanel.test.tsx can pin the literal values against drift.
export const SIGNATURE_BLOCK_MARKER_ATTR = 'data-ahoy-signature';
export const SIGNATURE_DELIMITER_HTML = '<p>--</p>';
